/**
 * studio/src/lib/auth.ts
 *
 * Pinia auth store — follows the Vue + Pinia pattern from the Appwrite docs.
 * Handles:
 *   - Account row refresh after name/email/password mutations
 *   - Magic link send
 *   - Session creation from secret (callback URL)
 *   - Route guarding
 *   - Creator record get-or-create on login
 */

import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { account, getCurrentUser, sendMagicLink, createSessionFromSecret, deleteCurrentSession, getCreatorByUserId } from "./appwrite";
import type { Models } from "appwrite";
import { usePitchesStore } from "../stores/usePitchesStore";
import { useMediaStore } from "../stores/useMediaStore";
import { toUserFacingError, type UserFacingError } from "./user-facing-error";

export const useAuthStore = defineStore("auth", () => {
  // ─── State ──────────────────────────────────────────────────────────────
  // `Models.User` is generic in the v16 SDK — `<Preferences>` defaults
  // to `DefaultPreferences`, but TS requires us to spell it out when
  // we declare the ref. We don't read any user preferences in the
  // studio today, so `Record<string, unknown>` is the honest type
  // for the shape we actually carry.
  const user = ref<Models.User<Record<string, unknown>> | null>(null);
  const loading = ref(false);
  const error = ref<UserFacingError | null>(null);
  const initialized = ref(false);

  // 2026-06-24 (profile menu): the Appwrite `creators` row is the
  // source of truth for avatar + display name (the Appwrite user
  // row carries email but not avatar). We cache the row here so the
  // profile menu + the /account view don't re-fetch on every route
  // change. `creatorUserId` is the key — when the user logs out we
  // clear both.
  const creator = ref<Record<string, unknown> | null>(null);
  const creatorUserId = ref<string | null>(null);
  const creatorLoading = ref(false);

  // ─── Getters ───────────────────────────────────────────────────────────
  const isLoggedIn = computed(() => !!user.value);
  const userId = computed(() => user.value?.$id ?? null);
  const userEmail = computed(() => user.value?.email ?? null);
  // The Appwrite user's `name` field (set at account-create time).
  // Distinct from the creator-scoped `creators.displayName` — the
  // profile menu composes the visible label from both.
  const userName = computed(() => user.value?.name ?? "");

  // ─── Actions ──────────────────────────────────────────────────────────

  /** Synchronous read of the cached creators row, if any. */
  function getCachedCreator(): Record<string, unknown> | null {
    return creator.value;
  }

  /**
   * Fetch the `creators` row for the given userId and cache it.
   * Idempotent — a second call for the same userId returns the
   * cached value without re-fetching. A not-found row caches as
   * `null` (a logged-in user mid-onboarding may have no creators
   * row yet); the menu must still render in that case.
   */
  async function loadCreatorForUser(uid: string): Promise<void> {
    if (!uid) return;
    if (creatorLoading.value) return;
    if (creatorUserId.value === uid) return; // cache hit
    creatorLoading.value = true;
    try {
      const row = await getCreatorByUserId(uid);
      creator.value = (row as Record<string, unknown> | null) ?? null;
      creatorUserId.value = uid;
    } catch {
      // Network / permission failure — keep the previous cache
      // rather than overwriting it with null. The menu will show
      // the Appwrite-Avatars fallback until the next load succeeds.
    } finally {
      creatorLoading.value = false;
    }
  }

  /** Invalidate the creators cache. Called by Settings.vue after a
   *  successful save so the profile menu reflects the new avatar /
   *  display name on the next render. */
  function clearCreatorCache(): void {
    creator.value = null;
    creatorUserId.value = null;
  }

  /** Refresh the account row after an authenticated account mutation. */
  async function refreshCurrentUser(): Promise<void> {
    user.value = await getCurrentUser();
  }

  /** Call on app mount — checks for existing session */
  async function init() {
    if (initialized.value) return;
    initialized.value = true;
    loading.value = true;
    error.value = null;
    try {
      user.value = await getCurrentUser();
    } catch {
      user.value = null;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Send a magic link to the given email.
   * @param email  Recipient email
   * @param redirectUrl  Where the magic link lands — must include the current origin
   *                    e.g. `${window.location.origin}/auth/callback`
   */
  async function requestMagicLink(email: string, redirectUrl: string) {
    loading.value = true;
    error.value = null;
    try {
      await sendMagicLink(email, redirectUrl);
    } catch (err: unknown) {
      error.value = toUserFacingError(err, { fallbackHeadline: "Failed to send magic link. Try again." });
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Complete login from the magic-link callback URL.
   * Call from /auth/callback with ?userId=...&secret=... query params.
   */
  async function completeMagicLogin(userId: string, secret: string) {
    loading.value = true;
    error.value = null;
    try {
      await createSessionFromSecret(userId, secret);
      user.value = await getCurrentUser();
    } catch (err: unknown) {
      error.value = toUserFacingError(err, { fallbackHeadline: "Login failed. Try again." });
      user.value = null;
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function logout() {
    loading.value = true;
    error.value = null;
    try {
      await deleteCurrentSession();
    } catch (err: unknown) {
      // Session may already be gone — clear state regardless
    } finally {
      user.value = null;
      clearCreatorCache();
      usePitchesStore().clearCache();
      useMediaStore().clearCache();
      loading.value = false;
    }
  }

  return {
    // state
    user,
    loading,
    error,
    initialized,
    creator,
    creatorLoading,
    // getters
    isLoggedIn,
    userId,
    userEmail,
    userName,
    // actions
    init,
    requestMagicLink,
    completeMagicLogin,
    refreshCurrentUser,
    logout,
    loadCreatorForUser,
    getCachedCreator,
    clearCreatorCache,
  };
});
